import { useEffect, useState } from "react";
import "../assets/styles/TenantList.css";

export default function TenantList() {
    const [tenants, setTenants] = useState([]);
    const [error, setError] = useState(null);
    const [editingTenant, setEditingTenant] = useState(null);
    const [formData, setFormData] = useState({ customer_name: "", quota: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("tenant_id");
    const [sortDirection, setSortDirection] = useState("asc");
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage] = useState(20);
    const [totalTenants, setTotalTenants] = useState(0);
    const [selectedTenantRequests, setSelectedTenantRequests] = useState(null);
    const [requests, setRequests] = useState([]);
    const [selectedTenantOrders, setSelectedTenantOrders] = useState(null);
    const [orders, setOrders] = useState([]);

    const months = [
        "Januar", "Februar", "März", "April", "Mai", "Juni",
        "Juli", "August", "September", "Oktober", "November", "Dezember"
    ];

    const columns = [
        { label: "Tenant ID", field: "tenant_id" },
        { label: "Kundennummer", field: "customer_no" },
        { label: "Kundenname", field: "customer_name" },
        { label: "AI Quota", field: "quota" },
        { label: "AI Verbrauch", field: "used" },
        { label: "Bestellungen", field: "orders" },
        { label: "Aktionen", field: null }
    ];

    useEffect(() => {
        if (!selectedTenantRequests && !selectedTenantOrders) fetchTenants();
    }, [month, year, currentPage, sortColumn, sortDirection, searchTerm, selectedTenantRequests, selectedTenantOrders]);

    const fetchTenants = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants?month=${month}&year=${year}&page=${currentPage}&limit=${entriesPerPage}&sortField=${sortColumn}&sortOrder=${sortDirection}&search=${encodeURIComponent(searchTerm)}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": process.env.REACT_APP_API_KEY
                    }
                }
            );

            if (!response.ok) throw new Error("Fehler beim Abrufen der Tenants");

            const json = await response.json();
            setTenants(json.data);
            setTotalTenants(json.total);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchRequestsForTenant = async (tenantId) => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/requests/tenant/${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": process.env.REACT_APP_API_KEY
                    }
                }
            );
            if (!response.ok) throw new Error("Fehler beim Abrufen der Requests");

            const json = await response.json();
            setRequests(json);
            setSelectedTenantRequests(tenantId);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrdersForTenant = async (tenantId) => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/orders/tenant/${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": process.env.REACT_APP_API_KEY
                    }
                }
            );
            if (!response.ok) throw new Error("Fehler beim Abrufen der Bestellungen");

            const json = await response.json();
            setOrders(json);
            setSelectedTenantOrders(tenantId);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (tenant) => {
        setEditingTenant(tenant.tenant_id);
        setFormData({
            customer_name: tenant.customer_name || "",
            quota: tenant.quota || ""
        });
    };

    const handleCancelEdit = () => {
        setEditingTenant(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (tenantId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/${tenantId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": process.env.REACT_APP_API_KEY
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error("Fehler beim Aktualisieren");

            await fetchTenants();
            setEditingTenant(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleMonthChange = (e) => {
        setMonth(e.target.value);
        setCurrentPage(1);
    };

    const handleYearChange = (e) => {
        setYear(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleBack = () => {
        setSelectedTenantRequests(null);
        setSelectedTenantOrders(null);
        setRequests([]);
    };

    const handleExportCsv = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants?month=${month}&year=${year}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": process.env.REACT_APP_API_KEY
                    }
                }
            );

            if (!response.ok) throw new Error("Fehler beim Abrufen der Tenants für den Export");

            const json = await response.json();
            const exportRows = json.data.map((t) => {
                const used = parseFloat(t.used) || 0;
                const quota = parseFloat(t.quota) || 0;
                let count = used - quota;
                const orders = parseInt(t.orders) || 0;
                if (count < 0) count = 0;
                return [t.tenant_id, t.customer_no, count, orders];
            });

            const headers = ["tenant_id", "customer_no", "ai_usage", "orders"];
            const csvContent = [headers, ...exportRows]
                .map(row => row.join(","))
                .join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `customer_usage_${month}_${year}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSendMail = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants/export/mail?month=${month}&year=${year}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": process.env.REACT_APP_API_KEY
                    }
                }
            );

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || "Fehler beim E-Mail-Versand");
            }

            alert("E-Mail wurde erfolgreich gesendet.");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };



    return (
        <div className="tenant-container">
            <h2>{selectedTenantOrders ? "Bestellungen anzeigen" : selectedTenantRequests ? "Requests anzeigen" : "Tenants"}</h2>
            {error && <div className="error-message">{error}</div>}

            {!selectedTenantOrders && !selectedTenantRequests && (
                <div className="filter-bar">
                    <select onChange={handleMonthChange} value={month}>
                        {months.map((monthName, index) => (
                            <option key={index} value={index + 1}>
                                {monthName}
                            </option>
                        ))}
                    </select>
                    <select onChange={handleYearChange} value={year}>
                        {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 10 + i).map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Suchen..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
            )}

            {loading ? (
                <div className="loading">Lädt...</div>
            ) : selectedTenantRequests || selectedTenantOrders ? (
                <div>
                    <button onClick={handleBack} className="cancel-btn">Zurück</button>
                    <ul className="tenant-list">
                        {selectedTenantRequests ? (requests.map((req) => (
                            <li key={req.id}>
                                <strong>{req.created_at}</strong><br />
                                <em>{req.question}</em>
                                <div>{req.answer}</div>
                                {req.deployment && <small>System: {req.deployment}</small>}
                            </li>
                        ))) : (
                            orders.map((order) => (
                                <li key={order.id}>
                                    <strong>{order.created_at}</strong><br />
                                    <em>{order.orderNo}</em>
                                    <div>{order.company}</div>
                                    <div>{order.shopCode}</div>
                                    <div>{order.languageCode}</div>
                                    <div>{order.total}</div>
                                    <div>{order.paymentMethod}</div>
                                    <div>{order.shopUrl}</div>
                                    {order.deployment && <small>System: {order.deployment}</small>}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            ) : (
                <div>
                    <div className="export-bar">
                        <button onClick={handleExportCsv} className="export-btn">
                            Debitor Verbräuche herunterladen
                        </button>
                        <button onClick={handleSendMail} className="export-btn">
                            Debitor Verbräuche per E-Mail senden
                        </button>
                    </div>
                    <div className="tenant-grid">
                        <div className="grid-header">
                            {columns.map(({ label, field }) => (
                                <div
                                    key={label}
                                    onClick={() => field && handleSort(field)}
                                    style={{ cursor: field ? "pointer" : "default" }}
                                >
                                    {label}
                                    {sortColumn === field && (
                                        <span style={{ marginLeft: 5 }}>
                                            {sortDirection === "asc" ? "▲" : "▼"}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {tenants.map((tenant) => (
                            <div className="tenant-row" key={tenant.tenant_id}>
                                <div>{tenant.tenant_id}</div>
                                <div>{tenant.customer_no}</div>

                                {editingTenant === tenant.tenant_id ? (
                                    <>
                                        <div>
                                            <input
                                                type="text"
                                                name="customer_name"
                                                value={formData.customer_name}
                                                onChange={handleInputChange}
                                                className="edit-input"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="number"
                                                name="quota"
                                                value={formData.quota}
                                                onChange={handleInputChange}
                                                className="edit-input"
                                            />
                                        </div>
                                        <div>{tenant.used || "Keine Nutzung"}</div>
                                        <div>{tenant.orders || "Keine Bestellungen"}</div>
                                        <div className="actions">
                                            <button
                                                onClick={() => handleSubmit(tenant.tenant_id)}
                                                className="save-btn"
                                            >
                                                Speichern
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                className="cancel-btn"
                                            >
                                                Abbrechen
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>{tenant.customer_name || "Kein Name"}</div>
                                        <div>{tenant.quota || "Keine Quota"}</div>
                                        <div>{tenant.used || "Keine Nutzung"}</div>
                                        <div>{tenant.orders || "Keine Bestellungen"}</div>
                                        <div className="actions">
                                            <button
                                                onClick={() => handleEditClick(tenant)}
                                                className="edit-btn"
                                            >
                                                Bearbeiten
                                            </button>
                                            <button
                                                onClick={() => fetchRequestsForTenant(tenant.tenant_id)}
                                                className="save-btn"
                                            >
                                                Requests anzeigen
                                            </button>
                                            <button
                                                onClick={() => fetchOrdersForTenant(tenant.tenant_id)}
                                                className="save-btn"
                                            >
                                                Bestellungen anzeigen
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!selectedTenantOrders && !selectedTenantRequests && (
                <div className="pagination">
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        {"<"}
                    </button>
                    <span>
                        Seite {currentPage} von {Math.ceil(totalTenants / entriesPerPage)}
                    </span>
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === Math.ceil(totalTenants / entriesPerPage)}
                    >
                        {">"}
                    </button>
                </div>
            )}
        </div>
    );
}
